"use client";

import { useState, useEffect } from "react";
import { ArrowRight, Heart } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import HomeAppointmentModal from "@/pages/Home/sections/HomeAppointmentModal";
import { useReducedMotion } from "framer-motion";
export default function Cardiovascular() {
  const [mounted, setMounted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  useEffect(() => {
    setMounted(true);
  }, []);
  const specTags = [{
    label: "[EST. 2008]"
  }, {
    label: "[BOARD CERTIFIED]"
  }, {
    label: "DELRAY BEACH AND BOYNTON BEACH"
  }];
  const content = {
    eyebrow: "FLORIDA PREMIER CARDIOLOGY",
    title: "Cardiovascular care, guided with precision",
    description: "Personalized heart care for Delray Beach and Boynton Beach, from your first visit through every step that follows."
  };
  const getFadeUpStyle = (delayMs: number) => ({
    opacity: contentVisible ? 1 : 0,
    transform: contentVisible ? "translateY(0)" : "translateY(20px)",
    transition: `opacity 400ms cubic-bezier(0.16, 1, 0.3, 1) ${delayMs}ms, transform 400ms cubic-bezier(0.16, 1, 0.3, 1) ${delayMs}ms`
  });
  const ekgPath = "M 0 60 L 80 60 L 90 55 L 96 70 L 104 15 L 112 105 L 118 60 L 125 55 L 130 60 L 280 60 L 290 55 L 296 70 L 304 15 L 312 105 L 318 60 L 325 55 L 330 60 L 480 60 L 490 55 L 496 70 L 504 15 L 512 105 L 518 60 L 525 55 L 530 60 L 600 60";
  return <section data-section-id="1765" id="world-class-cardiovascular-care" className="bg-white text-slate-900 relative w-full overflow-hidden border-b border-slate-200">
      <div className="container mx-auto px-6 pt-7 pb-8 sm:pt-9 sm:pb-[36px] max-w-7xl">
        <div ref={contentRef} className="flex flex-col">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6 flex flex-col relative">
              <div className="relative z-10 flex items-center gap-3 mb-2">
                <span
                  aria-hidden="true"
                  className="inline-block w-3 h-3 bg-[#a32d2d] rounded-sm shrink-0"
                  style={{
                    opacity: contentVisible || reduceMotion ? 1 : 0,
                    transform: contentVisible || reduceMotion ? "scale(1) rotate(0deg)" : "scale(0.35) rotate(-45deg)",
                    transition: reduceMotion ? "none" : "opacity 350ms ease-out, transform 600ms cubic-bezier(0.16, 1, 0.3, 1)"
                  }}
                />
                <span className="overflow-hidden py-1 -my-1">
                  <span
                    className="inline-block font-mono text-base sm:text-lg font-bold uppercase text-[#152238]"
                    style={{
                      opacity: contentVisible || reduceMotion ? 1 : 0,
                      transform: contentVisible || reduceMotion ? "translateY(0)" : "translateY(115%)",
                      letterSpacing: contentVisible || reduceMotion ? "0.12em" : "0.2em",
                      transition: reduceMotion ? "none" : "opacity 450ms ease-out 100ms, transform 800ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, letter-spacing 900ms cubic-bezier(0.16, 1, 0.3, 1) 100ms"
                    }}
                  >
                    {content.eyebrow}
                  </span>
                </span>
              </div>

              <div className="relative w-full h-8 sm:h-10 mt-2 mb-3 sm:mt-3 sm:mb-4 overflow-hidden pointer-events-none" aria-hidden="true">
                <div className="absolute inset-0 opacity-30">
                  <svg viewBox="0 0 600 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" preserveAspectRatio="none">
                    <path d={ekgPath} stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" pathLength="1" style={{
                    strokeDasharray: 1,
                    strokeDashoffset: mounted ? 0 : 1,
                    transition: "stroke-dashoffset 1.8s ease-out"
                  }} />
                  </svg>
                </div>

                <div className="absolute inset-0 z-[1]">
                  <svg viewBox="0 0 600 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" preserveAspectRatio="none">
                    <circle r="4.2" fill="#a32d2d">
                      <animateMotion path={ekgPath} dur="5.6s" begin="1.8s" repeatCount="indefinite" rotate="auto" />
                    </circle>
                  </svg>
                </div>
              </div>

              <h1 className="relative z-10 font-serif font-medium text-[#152238] mb-4 text-[36px] sm:text-[38px] lg:text-[40px] tracking-tight leading-[1.2]" style={getFadeUpStyle(100)}>
                {content.title}
              </h1>

              <div className="relative z-10 flex items-center gap-x-2.5 mb-4 text-xs font-mono" style={getFadeUpStyle(150)}>
                <Heart className="size-4 text-[#a32d2d] fill-[#a32d2d] animate-pulse shrink-0" />
                <span className="font-semibold text-sm text-[#152238]">72</span>
                <span className="text-slate-500 text-xs">BPM</span>
                <span className="text-slate-300 mx-0.5">•</span>
                <span className="inline-block w-1.5 h-1.5 bg-[#a32d2d] rounded-sm shrink-0" />
                <span className="font-semibold text-[11px] uppercase tracking-wider text-[#a32d2d]">LIVE</span>
              </div>

              <div className="relative z-10" style={getFadeUpStyle(200)}>
                <p className="font-default text-base sm:text-lg text-slate-600 mb-8 max-w-[62ch] leading-relaxed">
                  {content.description}
                </p>
              </div>

              <div className="relative z-10 flex flex-wrap items-center gap-4 mb-10" style={getFadeUpStyle(300)}>
                <Button size="lg" onClick={() => setIsModalOpen(true)} className="h-14 rounded-lg font-default font-semibold uppercase tracking-[0.06em] text-xs px-6 py-3.5 bg-[#a32d2d] text-white hover:bg-[#7f2424] shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer">
                  Schedule appointment
                  <ArrowRight className="ml-1 size-4" />
                </Button>

                <Link to="#integratedclinicaloverview">
                  <Button variant="outline" size="lg" className="h-14 rounded-lg font-default font-semibold uppercase tracking-[0.06em] text-xs px-6 py-3.5 border-slate-300 bg-white text-[#152238] hover:bg-slate-100 hover:text-[#152238] transition-all duration-200 hover:-translate-y-0.5">
                    Meet Dr. Carrillo
                  </Button>
                </Link>
              </div>

              <div className="relative z-10 flex flex-wrap lg:flex-nowrap whitespace-nowrap items-center gap-x-6 gap-y-2 pt-4 border-t border-slate-200">
                {specTags.map((tag, i) => <span key={i} data-index={i} className={`font-mono text-xs font-semibold tracking-[0.12em] uppercase ${i === 0 ? "text-[#a32d2d]" : "text-slate-500"}`}>
                    {tag.label}
                  </span>)}
              </div>
            </div>

            <div className="w-full lg:col-span-6 flex flex-col items-center justify-center pt-2 lg:pt-0">
              <div className="relative w-full sm:max-w-[560px] lg:max-w-none mx-auto" style={getFadeUpStyle(200)}>
                <div className="relative z-10 aspect-[3/2] w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-none">
                  <div className="absolute inset-y-0 left-0 w-[20%] bg-gradient-to-r from-white via-white/0 to-transparent z-10 pointer-events-none" aria-hidden="true" />
                  <img src="/react-current/images/site/dr-carrillo-hero-scanner-hq.jpg" alt="Dr. Carrillo with a patient during a PET/CT scan" className="w-full h-full object-cover object-center" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HomeAppointmentModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>;
}
